import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../services/api';

const QrRedirectPage = () => {
  const { lang, code } = useParams();
  const navigate = useNavigate();
  
  // Default to 'mk' language if no language prefix (for engraved QR codes)
  const defaultLang = lang || 'mk';
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userData = await fetchWithAuth('/users/me');
          setUser(userData);
        } catch (err) {
          console.error('Error fetching user:', err);
          // User is not authenticated
          setUser(null);
        }
      }
      setAuthLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const handleQrRedirect = async () => {
      if (authLoading) return; // Wait for auth to load
      
      try {
        // Check QR code status
        const response = await fetch(`/api/qr/${code}/status`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('Invalid QR code');
            setTimeout(() => navigate(`/${defaultLang}/`), 3000);
            return;
          }
          throw new Error('Failed to check QR code status');
        }

        const qrStatus = await response.json();

        if (qrStatus.assigned) {
          // Bare engraved QR links open in the profile's original language.
          // Language-prefixed QR links keep the visitor's explicit selection.
          const destinationLanguage = lang || qrStatus.profileLanguage || 'mk';
          navigate(`/${destinationLanguage}/profile/${qrStatus.profileStringId || qrStatus.profileId}?qr=${code}`);
        } else {
          // QR code is unassigned
          if (user) {
            // User is logged in, redirect to claiming page
            navigate(`/${defaultLang}/qr-claim`, { 
              state: { qrCode: code }
            });
          } else {
            // User not logged in, store QR code and redirect to login
            sessionStorage.setItem('qrCodeToClaim', code);
            navigate(`/${defaultLang}/login?qr=${code}`);
          }
        }
      } catch (err) {
        console.error('Error handling QR redirect:', err);
        setError('Error processing QR code');
        setTimeout(() => navigate(`/${defaultLang}/`), 3000);
      } finally {
        setLoading(false);
      }
    };

    handleQrRedirect();
  }, [code, defaultLang, navigate, user, authLoading]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Processing QR code...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">QR Code Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Redirecting to home page...</p>
        </div>
      </div>
    );
  }

  return null; // Should not reach here as we redirect in useEffect
};

export default QrRedirectPage;
